/**
 * Asset Management · Register & hierarchy.
 *
 * The site → zone → asset tree on the left, the register on the right,
 * filtered by the tree, a search, the type and the lifecycle stage. Every
 * asset opens to its own page. The coverage line leads because the register
 * is honest about what it does not hold yet: on the real Madinah estate no
 * asset has an install date, and a lifecycle screen that hid that would look
 * like "nothing is due".
 *
 * Import takes a CSV of lifecycle fields by tag — the realistic way the
 * missing dates and costs arrive, from a contractor's handover sheet — and
 * says exactly which rows it could not use.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { useAmImport, useAmRegister } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, ErrorState, Field, Input, Select, Skeleton, Table } from "../../design/components";
import {
  IMPORT_COLUMNS,
  parseImport,
  STAGE_LABEL,
  STAGES,
  type AmFilter,
  type AmImportResult,
  type AmRegister,
  type AmZone,
} from "../../domain/assetManagement";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { Coverage, Criticality, errorText, HealthChip, s, StageLabel, SUITE, Tiles } from "./shared";
import { AssetDialog } from "./AssetDialog";

export function AssetRegisterPage() {
  const session = useSession();
  const [filter, setFilter] = useState<AmFilter>({});
  const [importing, setImporting] = useState(false);
  /* the asset window: "new" to register one, an id to edit it */
  const [dialog, setDialog] = useState<string | null>(null);
  const navigate = useNavigate();
  const q = useAmRegister(filter);
  const reg = q.data;
  const canEdit = hasPermission(session.principal, PERMISSION.assetWrite);

  useScreenContext(
    reg
      ? {
          route: "/asset-register",
          title: "Asset register",
          summary: [
            t("{n} assets across {types} types and {zones} zones.", {
              n: reg.coverage.assets,
              types: reg.types.length,
              zones: reg.zones.length,
            }),
            t(
              "{dated} have an install date; {assessed} have a health score; {components} are components of another asset.",
              {
                dated: reg.coverage.dated,
                assessed: reg.coverage.assessed,
                components: reg.coverage.components,
              },
            ),
          ],
          offers: [
            { label: t("Which assets are critical?"), ask: "which assets are the most critical?" },
            {
              label: t("What is missing from the register?"),
              ask: "what lifecycle data is missing from the asset register?",
            },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page
      eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })}
      title="Asset register"
      actions={
        canEdit ? (
          <div className={s.actions}>
            <Button variant="primary" onClick={() => setDialog("new")} data-testid="am-register-new">
              {t("Register a new asset")}
            </Button>
            <Button onClick={() => setImporting((v) => !v)} data-testid="am-import-open">
              {t("Import lifecycle data")}
            </Button>
          </div>
        ) : undefined
      }
    >
      {dialog && (
        <AssetDialog
          assetId={dialog === "new" ? undefined : dialog}
          onClose={() => setDialog(null)}
          onSaved={(id) => navigate(`/asset-register/${encodeURIComponent(id)}`)}
        />
      )}
      {importing && <ImportCard onClose={() => setImporting(false)} />}
      {q.isLoading && <Skeleton rows={8} />}
      {q.error && <ErrorState error={q.error} retry={() => void q.refetch()} />}
      {reg && (
        <>
          <Tiles
            items={[
              { key: "assets", label: t("Assets"), value: reg.coverage.assets.toLocaleString("en") },
              { key: "types", label: t("Asset types"), value: reg.types.length },
              { key: "zones", label: t("Zones"), value: reg.zones.length },
              {
                key: "components",
                label: t("Components"),
                value: reg.coverage.components,
                sub: t("assets fitted inside another asset"),
              },
            ]}
          />
          <Coverage
            lines={[
              { label: t("have an install or purchase date"), of: reg.coverage.assets, n: reg.coverage.dated },
              { label: t("have a health score"), of: reg.coverage.assets, n: reg.coverage.assessed },
              { label: t("have a manufacturer recorded"), of: reg.coverage.assets, n: reg.coverage.withMaker },
            ]}
          />
          <div className={s.layout}>
            <Card title={t("Zones")} sub={t("Choose a zone to see what is in it and below it.")}>
              <ZoneTree
                zones={reg.zones}
                selected={filter.zoneId}
                onSelect={(zoneId) => setFilter({ ...filter, zoneId })}
              />
            </Card>
            <Card
              title={t("Register")}
              sub={
                reg.truncated
                  ? t("Showing {n} of {total}; narrow the filter to see the rest.", {
                      n: reg.assets.length,
                      total: reg.total,
                    })
                  : t("{n} assets", { n: reg.total })
              }
            >
              <Filters reg={reg} filter={filter} onChange={setFilter} />
              <RegisterTable reg={reg} onEdit={canEdit ? setDialog : undefined} />
            </Card>
          </div>
        </>
      )}
    </Page>
  );
}

function Filters({ reg, filter, onChange }: { reg: AmRegister; filter: AmFilter; onChange: (f: AmFilter) => void }) {
  return (
    <div className={s.filters}>
      <Field label="Search">
        <Input
          value={filter.q ?? ""}
          placeholder={t("Tag, name or serial")}
          onChange={(e) => onChange({ ...filter, q: e.target.value })}
          data-testid="am-search"
        />
      </Field>
      <Field label="Type">
        <Select
          value={filter.assetType ?? ""}
          onChange={(e) => onChange({ ...filter, assetType: e.target.value || undefined })}
        >
          <option value="">{t("All types")}</option>
          {reg.types.map((ty) => (
            <option key={ty.key} value={ty.key}>
              {ty.name} ({ty.assets})
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Lifecycle stage">
        <Select
          value={filter.stage ?? ""}
          onChange={(e) => onChange({ ...filter, stage: e.target.value || undefined })}
        >
          <option value="">{t("All stages")}</option>
          {STAGES.map((st) => (
            <option key={st} value={st}>
              {t(STAGE_LABEL[st])}
            </option>
          ))}
        </Select>
      </Field>
      {(filter.q || filter.assetType || filter.stage || filter.zoneId) && (
        <Button size="sm" variant="ghost" onClick={() => onChange({})}>
          {t("Clear filters")}
        </Button>
      )}
    </div>
  );
}

function RegisterTable({ reg, onEdit }: { reg: AmRegister; onEdit?: (id: string) => void }) {
  if (!reg.assets.length) return <EmptyState>{t("No asset matches this filter.")}</EmptyState>;
  const rows = reg.assets.slice(0, 400);
  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>{t("Tag")}</th>
            <th>{t("Name")}</th>
            <th>{t("Type")}</th>
            <th>{t("Zone")}</th>
            <th>{t("Criticality")}</th>
            <th>{t("Health")}</th>
            <th>{t("Stage")}</th>
            <th>{t("Installed")}</th>
            {onEdit && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td className={s.mono}>
                <Link to={`/asset-register/${encodeURIComponent(a.id)}`} data-testid="am-asset-link">
                  {a.tag}
                </Link>
                {a.parentId && <span className={s.tag}>{t("component")}</span>}
              </td>
              <td>
                {a.name}
                {a.children > 0 && <span className={s.dim}> · {t("{n} components", { n: a.children })}</span>}
              </td>
              <td>{a.assetTypeName}</td>
              <td className={s.mono}>{a.zoneCode ?? "—"}</td>
              <td>
                <Criticality value={a.criticality} />
              </td>
              <td>
                <HealthChip band={a.healthBand} score={a.healthScore} />
              </td>
              <td>
                <StageLabel stage={a.stage} />
              </td>
              <td className={s.mono}>{a.installedOn ?? <span className={s.dim}>{t("not recorded")}</span>}</td>
              {onEdit && (
                <td>
                  <button type="button" className={s.linkBtn} onClick={() => onEdit(a.id)} data-testid="am-row-edit">
                    {t("Edit")}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {reg.assets.length > rows.length && (
        <p className={s.note}>
          {t("The first {n} are listed; search or filter to find the others.", { n: rows.length })}
        </p>
      )}
    </>
  );
}

function ZoneTree({
  zones,
  selected,
  onSelect,
}: {
  zones: AmZone[];
  selected?: string;
  onSelect: (id: string | undefined) => void;
}) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const children = useMemo(() => {
    const m = new Map<string | null, AmZone[]>();
    const ids = new Set(zones.map((z) => z.id));
    for (const z of zones) {
      const parent = z.parentId && ids.has(z.parentId) ? z.parentId : null;
      m.set(parent, [...(m.get(parent) ?? []), z]);
    }
    return m;
  }, [zones]);
  /* assets in a zone and everything under it */
  const totals = useMemo(() => {
    const out = new Map<string, number>();
    const walk = (z: AmZone): number => {
      const n = z.assets + (children.get(z.id) ?? []).reduce((sum, c) => sum + walk(c), 0);
      out.set(z.id, n);
      return n;
    };
    for (const z of children.get(null) ?? []) walk(z);
    return out;
  }, [children]);

  const render = (parent: string | null, depth: number) =>
    (children.get(parent) ?? []).map((z) => {
      const kids = children.get(z.id) ?? [];
      const isOpen = open.has(z.id);
      return (
        <div key={z.id}>
          <button
            type="button"
            className={s.treeItem}
            style={{ paddingInlineStart: 6 + depth * 14 }}
            aria-current={selected === z.id}
            onClick={() => {
              onSelect(selected === z.id ? undefined : z.id);
              if (kids.length) setOpen((o) => new Set(o).add(z.id));
            }}
          >
            <span
              className={s.treeToggle}
              onClick={(e) => {
                if (!kids.length) return;
                e.stopPropagation();
                setOpen((o) => {
                  const n = new Set(o);
                  if (n.has(z.id)) n.delete(z.id);
                  else n.add(z.id);
                  return n;
                });
              }}
            >
              {kids.length ? (isOpen ? "▾" : "▸") : ""}
            </span>
            <span>
              {z.name} <span className={s.dim}>{z.code}</span>
            </span>
            <span className={s.treeCount}>{totals.get(z.id) ?? z.assets}</span>
          </button>
          {isOpen && render(z.id, depth + 1)}
        </div>
      );
    });

  if (!zones.length) return <EmptyState>{t("This site has no zones yet.")}</EmptyState>;
  return (
    <div className={s.tree} data-testid="am-zone-tree">
      <button type="button" className={s.treeItem} aria-current={!selected} onClick={() => onSelect(undefined)}>
        <span className={s.treeToggle} />
        <span>{t("Whole site")}</span>
      </button>
      {render(null, 0)}
    </div>
  );
}

function ImportCard({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<AmImportResult | null>(null);
  const imp = useAmImport();
  const parsed = useMemo(() => (text.trim() ? parseImport(text) : null), [text]);

  const loadFile = async (f: File | undefined) => {
    if (f) setText(await f.text());
  };

  return (
    <Card
      title={t("Import lifecycle data")}
      sub={t("CSV with headers. Matches assets by tag; adds no new ones.")}
      actions={
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("Close")}
        </Button>
      }
    >
      <p className={s.note}>
        {t("Columns understood:")} <span className={s.mono}>{IMPORT_COLUMNS.join(", ")}</span>.{" "}
        {t("Dates as YYYY-MM-DD.")}
      </p>
      <div className={s.formFoot}>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => void loadFile(e.target.files?.[0])}
          data-testid="am-import-file"
        />
      </div>
      <textarea
        className={s.textarea}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setResult(null);
        }}
        placeholder={
          "tag,manufacturer,installed_on,design_life_years,replacement_value\nPMP-001,Grundfos,2014-05-01,15,38000"
        }
        aria-label={t("CSV to import")}
        data-testid="am-import-text"
      />
      {parsed && (
        <div className={s.note}>
          {t("{n} rows ready.", { n: parsed.rows.length })}
          {parsed.ignored.length > 0 && <> {t("Ignored columns: {list}.", { list: parsed.ignored.join(", ") })}</>}
          {parsed.problems.slice(0, 8).map((p) => (
            <div key={p} className={s.err}>
              {p}
            </div>
          ))}
        </div>
      )}
      <div className={s.formFoot}>
        <Button
          variant="primary"
          disabled={!parsed?.rows.length || imp.isPending}
          onClick={() => imp.mutate(parsed?.rows ?? [], { onSuccess: setResult })}
          data-testid="am-import-go"
        >
          {imp.isPending ? t("Importing…") : t("Import {n} rows", { n: parsed?.rows.length ?? 0 })}
        </Button>
        {imp.error && <span className={s.err}>{errorText(imp.error)}</span>}
        {result && (
          <span className={s.ok} data-testid="am-import-result">
            {t("{n} assets updated.", { n: result.updated })}
            {result.notFoundCount > 0 && (
              <span className={s.err}>
                {" "}
                {t("{n} tags not found: {list}", {
                  n: result.notFoundCount,
                  list: result.notFound.slice(0, 12).join(", "),
                })}
              </span>
            )}
          </span>
        )}
      </div>
    </Card>
  );
}
